/**
 * O CHIP de etiqueta, num lugar só.
 *
 * Ele existia copiado em **quatro** arquivos — `inbox/Lista.tsx`, `inbox/Fio.tsx`,
 * `painel/Ajustes.tsx` e `painel/Contatos.tsx` — com o mesmo literal
 * `{ backgroundColor: `${cor}22`, color: cor }`. Quatro cópias do mesmo defeito de contraste, e
 * quatro lugares para consertar. Agora é um.
 *
 * O cálculo e o porquê estão em `contraste.ts`; a escolha do tema está no `.etiqueta` do
 * `vidro.css`. Aqui só se junta as duas coisas.
 */

import { coresDaEtiqueta, superficiesDoTema } from "./contraste.js";

export interface EtiquetaProps {
  readonly nome: string;
  /** A cor que o cliente escolheu. `null` cai no chip neutro. */
  readonly cor?: string | null;
  /**
   * Classes de forma — espaçamento, tamanho de fonte, layout. A COR nunca vem por aqui.
   *
   * ⚠️ Cada chamador passa o SEU espaçamento, e a base não traz nenhum. Duas utilidades de padding
   * na mesma lista não se resolvem pela ordem em que aparecem na string: quem vence é a ordem no
   * CSS gerado. Uma base com `px-1.5` e um chamador com `px-2` seria um resultado imprevisível.
   */
  readonly className?: string;
  readonly titulo?: string;
  /** O que vai DEPOIS do nome, dentro do chip — o botão de remover, no fio. */
  readonly children?: React.ReactNode;
}

/** Só o que é comum aos quatro. Espaçamento e tamanho vêm do chamador — ver `className`. */
const FORMA = "rounded-full font-medium";

export function Etiqueta(props: EtiquetaProps) {
  const cores = coresDaEtiqueta(props.cor, superficiesDoTema());
  const classe = [FORMA, props.className ?? ""].join(" ").trim();

  /*
    Sem cor escolhida: o chip NEUTRO, com os tokens de sempre. Não se inventa uma cor — etiqueta sem
    cor é uma escolha do cliente tanto quanto uma com.
  */
  if (!cores) {
    return (
      <span
        className={classe}
        style={{ background: "var(--vidro-controle)", color: "var(--tinta)" }}
        {...(props.titulo ? { title: props.titulo } : {})}
      >
        {props.nome}
        {props.children}
      </span>
    );
  }

  return (
    <span
      className={`${classe} etiqueta`}
      // As DUAS variantes; quem escolhe por tema é o CSS. Ver o cabeçalho de `etiqueta.ts`.
      style={
        {
          "--etiqueta-fundo-claro": cores.claro.fundo,
          "--etiqueta-texto-claro": cores.claro.texto,
          "--etiqueta-fundo-escuro": cores.escuro.fundo,
          "--etiqueta-texto-escuro": cores.escuro.texto,
        } as React.CSSProperties
      }
      {...(props.titulo ? { title: props.titulo } : {})}
    >
      {props.nome}
      {props.children}
    </span>
  );
}
